// src/components/AttendanceToggle.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../config/supabase';

const { FiLoader, FiChevronDown, FiCheck, FiX, FiRotateCcw, FiFileText, FiSave } = FiIcons;

const MENU_WIDTH = 288;
const GAP = 8;
const EDGE = 8;

// --------- Simple in-memory caches (per tab) ----------
const statusesCacheBySchoolId = new Map(); // schoolId -> statuses[]
const schoolIdCacheByLessonId = new Map(); // lessonId -> schoolId

const outcomeColor = (outcome) => {
  switch (outcome) {
    case 'present':   return { dot: 'bg-green-500',  pill: 'bg-green-50 text-green-700 border-green-200' };
    case 'absent':    return { dot: 'bg-red-500',    pill: 'bg-red-50 text-red-700 border-red-200' };
    case 'cancelled': return { dot: 'bg-amber-500',  pill: 'bg-amber-50 text-amber-700 border-amber-200' };
    default:          return { dot: 'bg-gray-400',   pill: 'bg-gray-100 text-gray-700 border-gray-200' };
  }
};

/**
 * AttendanceToggle
 * - default: self-loads (legacy individual flow)
 * - compact + prefetch: instant dot for group bar (no per-tile loads)
 *
 * Props
 * @param {string} lessonId
 * @param {string} lessonsParticipantId
 * @param {boolean} disabled
 * @param {'default'|'compact'} variant
 * @param {{ schoolId?: string, statuses?: Array, initialStatusId?: string|null }} prefetch  // optional
 */
const AttendanceToggle = ({
  lessonId,
  lessonsParticipantId,
  disabled = false,
  variant = 'default',
  prefetch = {}
}) => {
  const [loading, setLoading] = useState(variant === 'default'); // compact+prefetch skips loader by default
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const [commentOpen, setCommentOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  
  const [schoolId, setSchoolId] = useState(prefetch.schoolId ?? null);
  const [statuses, setStatuses] = useState(() => {
    const sid = prefetch.schoolId ?? null;
    if (prefetch.statuses?.length) return prefetch.statuses;
    if (sid && statusesCacheBySchoolId.has(sid)) return statusesCacheBySchoolId.get(sid);
    return [];
  });

const [attendance, setAttendance] = useState(() => {
  if (!prefetch.initialStatusId) return null;

  const sid = prefetch.schoolId ?? null;
  const initialStatuses =
    prefetch.statuses?.length
      ? prefetch.statuses
      : sid && statusesCacheBySchoolId.has(sid)
        ? statusesCacheBySchoolId.get(sid)
        : [];

  return {
    id: null,
    status_id: prefetch.initialStatusId,
    outcome: deriveOutcome(prefetch.initialStatusId, initialStatuses),
    label: null,
    comment: null,
  };
});

  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const bcRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ left: 0, top: 0, maxHeight: 320 });

  useEffect(() => {
    const bc = new BroadcastChannel('mms-lessons');
    bcRef.current = bc;
    return () => bc.close();
  }, []);

  // ---------- Self-load ONLY when not prefetched (keeps individual flow unchanged) ----------
  useEffect(() => {
    let cancelled = false;
    if (!lessonId || !lessonsParticipantId) {
      setLoading(false);
      return;
    }
    if ((prefetch?.statuses?.length ?? 0) > 0 && prefetch?.schoolId !== undefined) {
      // We were prehydrated – no loads.
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        // 1) Resolve schoolId (prefer prop/prefetch/cache, fallback to DB)
        let sid = prefetch.schoolId ?? schoolIdCacheByLessonId.get(lessonId) ?? null;
        if (!sid) {
          const { data: lesson, error: lessonErr } = await supabase
            .from('lessons')
            .select('id, school_id')
            .eq('id', lessonId)
            .single();
          if (lessonErr) throw lessonErr;
          sid = lesson.school_id ?? null;
          if (sid) schoolIdCacheByLessonId.set(lessonId, sid);
        }
        if (cancelled) return;
        setSchoolId(sid);

        // 2) Load statuses (from cache if possible)
        let sts = statusesCacheBySchoolId.get(sid) ?? null;
        if (!sts) {
          const { data: stsData, error: stsErr } = await supabase
            .from('attendance_statuses')
            .select('id,label,outcome,allows_makeup,counts_toward_quota_default,billable_default,is_active,sort_order')
            .eq('school_id', sid)
            .eq('is_active', true)
            .order('sort_order', { ascending: true })
            .order('label', { ascending: true });
          if (stsErr) throw stsErr;
          sts = stsData || [];
          statusesCacheBySchoolId.set(sid, sts);
        }
        if (cancelled) return;
        setStatuses(sts);

        // 3) Load current attendance via RPC (avoids RLS-heavy attendance SELECT)
        const fetchAttendanceOnce = async () =>
          supabase.rpc('get_attendance_for_lessons_participant', {
            p_lessons_participant_id: lessonsParticipantId
          });

        // Retry on transient statement timeout (57014) — common during load spikes.
        let attRes = await fetchAttendanceOnce();
        if (attRes?.error?.code === '57014') {
          await new Promise(r => setTimeout(r, 250));
          attRes = await fetchAttendanceOnce();
        }

        const attErr = attRes.error;
        if (attErr) throw attErr;

        // RPC returns an array of rows (0..1) for "returns table"
        const row = Array.isArray(attRes.data) ? attRes.data[0] : null;
        if (cancelled) return;
        setAttendance(row ? { ...row, status_id: row.attendance_status_id } : null);
      } catch (e) {
        console.error('Attendance load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [lessonId, lessonsParticipantId, prefetch?.statuses, prefetch?.schoolId]);

  // keep draft in sync when we load or change attendance rows
  useEffect(() => {
    setCommentDraft(attendance?.comment ?? '');
  }, [attendance?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When parent changes prefetched status (optimistic update), reflect it
  useEffect(() => {
    if (prefetch?.initialStatusId !== undefined) {
      setAttendance(prev => {
        const status_id = prefetch.initialStatusId;
        if (prev?.status_id === status_id) return prev;
        return status_id
          ? { id: prev?.id ?? null, status_id, outcome: deriveOutcome(status_id, statuses), label: prev?.label ?? null, comment: prev?.comment ?? null }
          : null;
      });
    }
  }, [prefetch?.initialStatusId, statuses]);

  const selectedStatus = useMemo(() => {
    if (!attendance?.status_id) return null;
    return statuses.find(s => s.id === attendance.status_id) || null;
  }, [attendance, statuses]);

  // ---------- Menu positioning and events ----------
  useEffect(() => {
    if (!menuOpen) return;
    const computeMenuPosition = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = Math.min(rect.right - MENU_WIDTH, vw - MENU_WIDTH - EDGE);
      left = Math.max(left, EDGE);
      const spaceBelow = vh - rect.bottom - GAP - EDGE;
      const spaceAbove = rect.top - GAP - EDGE;
      let top = rect.bottom + GAP;
      let maxHeight = Math.max(160, Math.min(320, spaceBelow));
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        maxHeight = Math.max(160, Math.min(320, spaceAbove));
        top = rect.top - GAP - maxHeight;
      }
      setMenuPos({ left: Math.round(left), top: Math.round(top), maxHeight: Math.round(maxHeight) });
    };

    computeMenuPosition();
    window.addEventListener('scroll', computeMenuPosition, { passive: true });
    window.addEventListener('resize', computeMenuPosition);
    return () => {
      window.removeEventListener('scroll', computeMenuPosition);
      window.removeEventListener('resize', computeMenuPosition);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      const btn = buttonRef.current;
      const menu = menuRef.current;
      if (btn && !btn.contains(e.target) && menu && !menu.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  if (!lessonId || !lessonsParticipantId) return null;

  const closeComment = () => {
    setCommentOpen(false);
    setCommentDraft(attendance?.comment ?? '');
  };

  const saveComment = async () => {
    if (disabled || saving || commentSaving) return;
    if (!attendance?.id) return; // must have an attendance row first

    const nextComment = (commentDraft ?? '').trim();
    setCommentSaving(true);
    try {
      const { data, error } = await supabase
        .from('attendance')
        .update({ comment: nextComment.length ? nextComment : null })
        .eq('id', attendance.id)
        .select('comment')
        .single();
      if (error) throw error;

      setAttendance(prev => (prev ? { ...prev, comment: data?.comment ?? null } : prev));
      setCommentOpen(false);
      showToast('Comment saved', 'success');
    } catch (e) {
      console.error('attendance comment save failed:', e);
      showToast(e?.message || 'Failed to save comment', 'error');
    } finally {
      setCommentSaving(false);
    }
  };

  // ---------- Save via RPC (optimistic for speed) ----------
  const saveStatus = async (statusId) => {
  if (disabled || saving || !statusId) return;
  const prev = attendance;
  // optimistic update
  setAttendance({ ...(prev ?? {}), status_id: statusId, outcome: deriveOutcome(statusId, statuses), comment: prev?.comment ?? null });
  setMenuOpen(false);

  // fire-and-settle
  setSaving(true);
  try {
    const { data, error } = await supabase.rpc('mark_attendance_for_participant_v3', {
      p_lessons_participant_id: lessonsParticipantId,
      p_status_id: statusId,
      p_overrides: null,          // 👈 required third argument
    });
    if (error) throw error;

    setAttendance({
      id: data.id,
      status_id: data.attendance_status_id,
      outcome: data.outcome,
      counts_toward_quota: data.counts_toward_quota,
      billable: data.billable,
      makeup_eligible: data.makeup_eligible,
      marked_at: data.marked_at,
      label: data.label,
      comment: data.comment ?? prev?.comment ?? null,
    });

    bcRef.current?.postMessage({
      type: 'attendance_updated',
      lessonId,
      lessonsParticipantId,
      payload: {
        statusId: data.attendance_status_id ?? statusId,
        label: data.label ?? null,
        outcome: data.outcome ?? deriveOutcome(statusId, statuses),
      },
    });
  } catch (e) {
    console.error('mark_attendance_for_participant_v3 failed:', e);
    // revert on failure
    setAttendance(prev);
    showToast(e?.message || 'Failed to save attendance', 'error');
  } finally {
    setSaving(false);
  }
};

  const clearAttendance = async () => {
    if (disabled || saving) return;
    const prev = attendance;
    setAttendance(null);
    setMenuOpen(false);
    setCommentOpen(false);

    setSaving(true);
    try {
      const { error } = await supabase.rpc('clear_attendance_for_participant', {
        p_lessons_participant_id: lessonsParticipantId
      });
      if (error) throw error;

      bcRef.current?.postMessage({
        type: 'attendance_updated',
        lessonId,
        lessonsParticipantId,
        payload: { statusId: null, label: null, outcome: null }
      });

      showToast('Attendance cleared', 'success');
    } catch (e) {
      console.error('clear_attendance_for_participant failed:', e);
      setAttendance(prev); // revert
      showToast(e?.message || 'Failed to clear attendance', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---------- UI Helpers ----------
  const showToast = (message, type) => {
    if (variant === 'compact') return; // silent in grid to avoid noise
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 px-4 py-2 rounded-md shadow-md z-[10000] ${
      type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => document.body.contains(toast) && document.body.removeChild(toast), 3000);
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <span className={variant === 'compact' ? 'inline-flex items-center justify-center h-7 w-7 rounded-full bg-gray-100' : 'inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-sm'}>
        <SafeIcon icon={FiLoader} className="text-gray-500 animate-spin text-xs" />
        {variant === 'default' && <span className="text-gray-600 ml-1">Loading…</span>}
      </span>
    );
  }

  if (!schoolId && !prefetch?.schoolId) {
    if (variant === 'compact') return null;
    return (
      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-sm border border-red-200">
        <SafeIcon icon={FiX} className="text-xs" />
        Couldn’t load school
      </span>
    );
  }

  if (!statuses.length) {
    if (variant === 'compact') return null;
    return (
      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-sm border border-amber-200">
        <SafeIcon icon={FiX} className="text-xs" />
        No attendance statuses configured
      </span>
    );
  }

  const dotOutcome = attendance?.outcome || selectedStatus?.outcome || null;
  const Button = (
    <motion.button
      ref={buttonRef}
      onClick={() => !disabled && setMenuOpen(v => !v)}
      disabled={disabled || saving}
      aria-expanded={menuOpen}
      className={
        variant === 'compact'
          ? [
              'h-7 w-7 inline-flex items-center justify-center rounded-full border',
              disabled || saving ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md cursor-pointer',
              'bg-white border-gray-200'
            ].join(' ')
          : [
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border',
              disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md cursor-pointer',
              outcomeColor(dotOutcome).pill
            ].join(' ')
      }
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      title="Select attendance status"
    >
      {variant === 'compact' ? (
        <span className={`w-2 h-2 rounded-full ${outcomeColor(dotOutcome).dot}`} />
      ) : (
        <>
          <span className={`w-2 h-2 rounded-full ${outcomeColor(dotOutcome).dot}`} />
          <span className="truncate max-w-[160px]">{selectedStatus?.label || 'Mark attendance'}</span>
          <SafeIcon icon={FiChevronDown} className="text-xs opacity-70" />
        </>
      )}
    </motion.button>
  );

  const Menu = menuOpen
    ? createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', left: `${menuPos.left}px`, top: `${menuPos.top}px`, width: `${MENU_WIDTH}px`, zIndex: 1000 }}
          className="rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          <ul
            className="divide-y divide-gray-100"
            style={{ maxHeight: `${menuPos.maxHeight}px`, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
          >
            {statuses.map(s => {
              const c = outcomeColor(s.outcome);
              const isSelected = attendance?.status_id === s.id;
              return (
                <li key={s.id}>
                  <button
                    onClick={() => saveStatus(s.id)}
                    disabled={saving}
                    className={`w-full flex items-start gap-3 px-3 py-2 text-left hover:bg-gray-50 ${saving ? 'opacity-60' : ''}`}
                  >
                    <span className={`mt-1 w-2 h-2 rounded-full ${c.dot}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{s.label}</span>
                        {isSelected && (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                            <SafeIcon icon={FiCheck} /> Selected
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        Outcome: <span className="font-medium">{s.outcome}</span> • Counts quota:{' '}
                        <span className="font-medium">{s.counts_toward_quota_default ? 'Yes' : 'No'}</span> • Billable:{' '}
                        <span className="font-medium">{s.billable_default ? 'Yes' : 'No'}</span> • Makeup:{' '}
                        <span className="font-medium">{s.allows_makeup ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
            <li>
              <button
                onClick={clearAttendance}
                disabled={saving}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 text-gray-700"
              >
                <SafeIcon icon={FiRotateCcw} />
                <span>Clear attendance</span>
              </button>
            </li>
          </ul>
        </div>,
        document.body
      )
    : null;

  const CommentModal =
    commentOpen && variant !== 'compact'
      ? createPortal(
          <div data-mms-modal="attendance-comment" className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onMouseDown={closeComment} aria-hidden="true" />
            <motion.div
              data-mms-modal="attendance-comment"
              initial={{ opacity: 0, scale: 0.98, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 6 }}
              className="relative w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-200 p-5"
              // ✅ Critical: prevent App.jsx onMouseDownCapture from stealing focus into TipTap behind the modal
              onMouseDownCapture={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Attendance comment"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                    <SafeIcon icon={FiFileText} className="text-gray-600" />
                  </span>
                  <div>
                    <div className="text-sm font-bold text-gray-800">Attendance comment</div>
                    <div className="text-xs text-gray-500">Press Enter to save (Shift+Enter for newline)</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeComment}
                  disabled={commentSaving}
                  className={`p-2 rounded-xl ${commentSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                  aria-label="Close"
                  title="Close"
                >
                  <SafeIcon icon={FiX} className="text-gray-500" />
                </button>
              </div>

              <textarea
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                onMouseDownCapture={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    saveComment();
                  }
                }}
                disabled={commentSaving}
                rows={4}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                placeholder="Add a quick note…"
                autoFocus
              />

              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={closeComment}
                  disabled={commentSaving}
                  className={`flex-1 px-4 py-2 rounded-xl text-sm ${
                    commentSaving ? 'opacity-50 cursor-not-allowed bg-gray-50 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveComment}
                  disabled={commentSaving}
                  className={`flex-1 px-4 py-2 rounded-xl text-sm text-white ${
                    commentSaving ? 'opacity-60 cursor-not-allowed bg-gray-400' : 'bg-gray-900 hover:bg-gray-800'
                  }`}
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {commentSaving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
                    Save
                  </span>
                </button>
              </div>
            </motion.div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={variant === 'compact' ? 'relative inline-flex items-center' : 'relative inline-flex items-center gap-2'}>
      {Button}

      {variant !== 'compact' && attendance && (
        <button
          type="button"
          onClick={() => !disabled && setCommentOpen(true)}
          disabled={disabled || saving}
          title={attendance?.comment ? 'Edit comment' : 'Add comment'}
          aria-label="Add or edit attendance comment"
          className={`
            inline-flex items-center justify-center h-8 w-8 rounded-full border text-xs
            ${disabled || saving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}
            border-gray-200 text-gray-600
          `}
        >
          <SafeIcon icon={FiFileText} className="text-sm" />
        </button>
      )}

      {variant !== 'compact' && attendance && (
        <button
          type="button"
          onClick={clearAttendance}
          disabled={disabled || saving}
          title="Clear attendance (reset to scheduled)"
          aria-label="Clear attendance"
          className={`
            inline-flex items-center justify-center h-8 w-8 rounded-full border text-xs
            ${disabled || saving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}
            border-gray-200 text-gray-600
          `}
        >
          {saving ? <SafeIcon icon={FiLoader} className="text-xs animate-spin" /> : <SafeIcon icon={FiRotateCcw} className="text-sm" />}
        </button>
      )}

      {variant !== 'compact' && !!attendance?.comment && (
        <span className="text-[11px] text-gray-400 max-w-[220px] truncate" title={attendance.comment}>
          {attendance.comment}
        </span>
      )}

      {Menu}
      {CommentModal}
    </div>
  );
};

function deriveOutcome(statusId, statuses = []) {
  const s = statuses?.find?.(x => x.id === statusId);
  return s?.outcome ?? null;
}

export default AttendanceToggle;